import {
  Box,
  Button,
  Divider,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { GlobalContext } from 'providers/Global';
import { useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import { ReactComponent as HederaLogo } from 'assets/hedera-hbar-logo.svg';
import { ReactComponent as HashpackLogo } from 'assets/hashpack-wallet-logo.svg';
import { ReactComponent as WalletIcon } from 'assets/wallet-icon.svg';
import { ReactComponent as GreenWalletIcon } from 'assets/wallet-icon-green.svg';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';
import { ReactComponent as ExternalIcon } from 'assets/external-icon.svg';
import { ReactComponent as AddressBookIcon } from 'assets/address-book-icon.svg';
import { ReactComponent as SettingsIcon } from 'assets/settings-icon.svg';
import { ReactComponent as HelpIcon } from 'assets/help-icon.svg';
import { HASHPACK_DOWNLOAD_URL, ModalType, SupportedNetworks } from 'utils/constants';
import { getNetworkColor } from 'utils/color';
import { useNetworkSwitchWarning } from 'hooks/useNetworkSwitchWarning';
import { getExplorerUrl } from 'utils/explorerUrl';
import { useToast } from 'hooks/useToast';
import copyToClipboard from 'copy-to-clipboard';

const styles = {
  popoverItem: {
    maxH: 20,
    p: 1.5,
    gap: 2,
    width: "full",
    display: "flex",
    cursor: "pointer",
    borderRadius: 8,
    alignItems: "center",
    bg: "#F9F9F9",
    _hover: {
      background: 'rgba(0, 0, 0, 0.08)',
      '& [data-radio]': {
        borderColor: 'rgba(0, 0, 0, 0.24)',
      },
    },
  },
  radio: (isSelected: boolean) => ({
    position: "absolute",
    top: "50%",
    right: 3,
    width: 5,
    height: 5,
    bg: "#fff",
    border: "2px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "100%",
    transform: "translateY(-50%)",
    ...(isSelected ? {} : { borderColor: "rgba(0, 0, 0, 0.12) !important" }),
    "::before": {
      content: '""',
      position: 'absolute',
      bg: isSelected ? "#2ECB3B" : "transparent",
      inset: "3px",
      borderRadius: "100%",
    }
  }),
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    px: 2,
    py: 2.5,
    w: "full",
    borderRadius: 8,
    textAlign: "left" as const,
    cursor: "pointer",
    transition: "background 0.15s",
    _hover: {
      bg: "blackAlpha.50",
    },
  },
};

interface IModalMenuProps {
  onClose: () => void;
}

export const ModalMenu = (props: IModalMenuProps) => {
  const { onClose } = props;

  const isMobile = useMediaQuery({ query: '(max-width: 576px)' });
  const { showToast } = useToast();

  const {
    connection: { accountId, disconnectWallet },
    connectedNetwork,
    updateConnectorNetwork,
    openModal,
  } = useContext(GlobalContext);

  const { handleSelection, WarningModal: NetworkWarningModal } = useNetworkSwitchWarning({
    isAuthenticated: !!accountId,
    updateConnectorNetwork,
  });

  const handleMenuItem = (action: () => void) => {
    onClose();
    action();
  };

  return (
    <>
    {NetworkWarningModal}
    <Modal
      isOpen
      isCentered
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent width={isMobile ? "80vw" : "400px"}>
        <ModalHeader pt={6} px={6} pb={0} display="flex" alignItems="center">
          <Text size="lg" variant="bodyBold">
            {accountId ? 'Account' : 'Connect Wallet'}
          </Text>
          <ModalCloseButton color="#565656" mt={3} mr={2} />
        </ModalHeader>
        <ModalBody pt={6} pb={8} px={6}>
          <Text size="sm" variant="semiBold" mb={2}>
            Network
          </Text>
          <Flex
            direction="column"
            rowGap={2}
          >
            {Object.entries(SupportedNetworks)
              .filter(([_, n]) => n !== SupportedNetworks.Previewnet)
              .map(([key, network]) =>
                <Flex
                  position="relative"
                  cursor={'pointer'}
                  height={50}
                  gap={2}
                  alignItems={'center'}
                  key={key}
                  sx={styles.popoverItem}
                  onClick={() => handleSelection(network)}
                >
                  <HederaLogo style={{ marginTop: '5px' }} />
                  <Text fontWeight={600} size="sm" color={getNetworkColor(network)}>{network}</Text>

                  <Box
                    sx={styles.radio(!!connectedNetwork && network.toLowerCase() === connectedNetwork)}
                    data-radio
                  />
                </Flex>
              )}
          </Flex>

          <Divider my={6} borderColor="blackAlpha.300" />

          <Text size="sm" variant="semiBold" mb={2}>
            Wallet
          </Text>

          <Flex
            flexDirection="column"
            gap={2}
          >
            {accountId ? (
              <Flex gap={3} flexDirection="column">
                <Flex
                  gap={2}
                  w="full"
                  alignItems="center"
                >
                  <GreenWalletIcon style={{ maxHeight: '24px', maxWidth: '24px' }} />
                  <Text mt="2px" size="sm" variant="bodyBold" mr="auto" isTruncated>
                    {accountId}
                  </Text>
                </Flex>

                {/* Copy + HashScan */}
                <Flex gap={1}>
                  <Box
                    as="button"
                    aria-label="Copy address"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p={1.5}
                    borderRadius={6}
                    transition="background 0.15s"
                    _hover={{ bg: 'blackAlpha.100' }}
                    onClick={() => {
                      copyToClipboard(accountId);
                      showToast('Address copied to clipboard', 'success', undefined, 'top');
                    }}
                  >
                    <CopyIcon style={{ width: 16, height: 16 }} />
                  </Box>
                  {getExplorerUrl(connectedNetwork!, 'account', accountId!) && (
                    <Link
                      href={getExplorerUrl(connectedNetwork!, 'account', accountId!)!}
                      isExternal
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      p={1.5}
                      borderRadius={6}
                      title="View in Explorer"
                      transition="background 0.15s"
                      _hover={{ bg: 'blackAlpha.100' }}
                    >
                      <ExternalIcon style={{ width: 16, height: 16 }} />
                    </Link>
                  )}
                </Flex>

                <Divider borderColor="blackAlpha.200" />

                {/* Menu items */}
                <Flex flexDirection="column">
                  <Box
                    as="button"
                    sx={styles.menuItem}
                    onClick={() => handleMenuItem(() => openModal(ModalType.AddressBookDrawer))}
                  >
                    <AddressBookIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                    <Text size="sm" fontWeight={500}>Address Book</Text>
                  </Box>
                  <Box
                    as="button"
                    sx={styles.menuItem}
                    onClick={() => handleMenuItem(() => openModal(ModalType.NotificationSettingsDrawer))}
                  >
                    <SettingsIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                    <Text size="sm" fontWeight={500}>Notification Settings</Text>
                  </Box>
                  <Box
                    as="button"
                    sx={styles.menuItem}
                    onClick={() => handleMenuItem(() => openModal(ModalType.FAQDrawer))}
                  >
                    <HelpIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                    <Text size="sm" fontWeight={500}>Help & FAQs</Text>
                  </Box>
                </Flex>

                <Divider borderColor="blackAlpha.200" />

                <Button
                  variant="secondary"
                  borderRadius={6}
                  onClick={() => handleMenuItem(disconnectWallet)}
                >
                  Disconnect
                </Button>
              </Flex>
            ) : <PopoverItems />}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
    </>
  );
};

ModalMenu.displayName = 'ModalMenu';


type TPopoverItems = {
  logo: JSX.Element;
  text: string;
  description?: string;
  clickHandler: () => void;
  isDisabled: boolean;
}

const PopoverItems = () => {
  const {
    connection: {
      connectToHashPack,
      connectViaWalletConnect,
      isHashPackExtensionAvailable,
    }
  } = useContext(GlobalContext);
  const { onClose } = useDisclosure();

  const handleHashPackConnection = async () => {
    if (isHashPackExtensionAvailable) {
      onClose();
      connectToHashPack();
    } else {
      window.open(HASHPACK_DOWNLOAD_URL, '_blank', 'noopener');
    }
  };

  const handleWalletConnectConnection = async () => {
    onClose();
    connectViaWalletConnect();
  };

  const popoverItems: TPopoverItems[] = [
    {
      text: 'HashPack',
      description: isHashPackExtensionAvailable ? 'Browser Extension' : 'Install Extension',
      logo: <HashpackLogo style={{ maxHeight: '40px' }} />,
      clickHandler: handleHashPackConnection,
      isDisabled: false
    },
    {
      text: 'WalletConnect',
      logo: <WalletIcon style={{ maxHeight: '32px', maxWidth: '32px' }} />,
      clickHandler: handleWalletConnectConnection,
      isDisabled: false
    },
  ];

  return (
    <Flex flexDirection="column" gap={2}>
      {popoverItems.map((item, idx) => {
        if (!item.isDisabled) {
          return (
            <Box
              key={`wallet-widget-x${idx}`}
              sx={styles.popoverItem}
              onClick={item.clickHandler}
            >
              {item.logo}
              <Flex flexDirection="column" flex={1}>
                <Text size="sm" fontWeight={600}>
                  {item.text}
                </Text>
                {item.description && (
                  <Text size="xs" color="gray.500">
                    {item.description}
                  </Text>
                )}
              </Flex>
            </Box>
          );
        }
        return null;
      })}
    </Flex>
  );
};
